import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../components/commonComponent/CommanTable';
import UserView from './UserView';
import CommonModal from '../../components/commonComponent/CommonModal';
import { FaEye, FaEdit, FaPlus } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from '../../utils/alertHelper';

// User Table Columns
const userFields = [
  { key: 'srNo', label: 'Sr.no' },
  { key: 'userId', label: 'User ID' },
  { key: 'name', label: 'Name' },
  { key: 'emailId', label: 'Email id' },
  { key: 'roleId', label: 'Role ID' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Action' },
];

// Role Table Columns
const roleFields = [
  { key: 'srNo', label: 'Sr.no' }, 
  { key: 'roleName', label: 'Role' },
  { key: 'description', label: 'Description' },
  { key: 'actions', label: 'Action' },
];

function UserRole() {
  const [activeTab, setActiveTab] = useState('user');
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalData, setUserModalData] = useState({
    firstName: '',
    lastName: '',
    emailId: '',
    roleId: '',
    status: 'Active',
    note: '',
    userType: localStorage.getItem("userType") || 'Admin',
  });
  const [userErrors, setUserErrors] = useState({});
  const [userSubmitting, setUserSubmitting] = useState(false);

  // Table Data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [viewUserModal, setViewUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  
  
  const navigate = useNavigate();
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [totalUsers, setTotalUsers] = useState(0);

  // no businessId usage - removed per request

  useEffect(() => {
    // fetch once on mount
    fetchRoles();
    fetchUsers(page, limit);
  }, []);

  // Fetch roles from API using axios
  async function fetchRoles() {
    try {
      const userType = localStorage.getItem('userType') || 'Admin';
      let url = `/admin/api/role`;
      const res = await axios.get(url);
      const data = res.data;
      if (data.Status === 1 && Array.isArray(data.roles)) {
        // For roles table, show all roles
        setRoles(data.roles.map((r, idx) => ({
          id: r._id,
          roleName: r.name,
          description: r.description || '',
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
        })));
  // Populate roleOptions for select inputs
  setRoleOptions(data.roles.map(r => ({ value: r._id, label: r.name, description: r.description || '' })));
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }

  // Fetch users from API using axios based on userType and business (supports pagination)
  async function fetchUsers(pageArg = page, limitArg = limit) {
    try {
      const userType = localStorage.getItem('userType') || 'Admin';
      const params = new URLSearchParams();
      params.append('userType', userType);
      params.append('page', pageArg);
      params.append('limit', limitArg);
      const url = `/api/user?${params.toString()}`;
      const res = await axios.get(url);
      const data = res.data;
      if (data.Status === 1 && Array.isArray(data.users)) {
        setUsers(data.users.map(u => ({
          id: u._id,
          userId: u.memberId || '',
          firstName: u.firstName,
          lastName: u.lastName,
          name: `${u.firstName} ${u.lastName}`,
          emailId: u.email,
          roleId: u.role?.name || u.role || '',
          status: typeof u.status === 'object' ? u.status.name : u.status,
          note: u.note || '',
          userType: u.userType || '',
        })));
        const total = data.total || data.count || data.totalItems || data.totalCount || data.meta?.total;
        setTotalUsers(Number(total) || data.users.length || 0);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
      setTotalUsers(0);
    }
  }

  // User Table Data
  const userTableData = users
    .filter(u =>
      u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.userId.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.emailId.toLowerCase().includes(userSearchTerm.toLowerCase())
    )
    .map((u, idx) => ({
      ...u,
      srNo: (page - 1) * limit + idx + 1,
      status: (
        <span
          className={`badge px-3 py-2 rounded-pill`}
          style={{
            backgroundColor: u.status === 'Active' ? '#28a745' : '#6c757d',
            color: '#fff',
            fontWeight: 500,
          }}
        >
          {u.status}
        </span>
      ),
      actions: (
        <div>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="View"
            onClick={() => {
              setSelectedUser(u);
              setViewUserModal(true);
            }}
          >
            <FaEye size={14} />
          </button>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => {
              // When editing, set the roleId in the modal data
              const editData = {
                ...u,
                roleId: u.roleId || u.roleAssigned // Use roleId for editing
              };
              setUserModalData(editData);
              setShowUserModal(true);
            }}
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    }));
   
  // Role Table Data
  const roleTableData = roles
    .filter(r =>
      r.roleName.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
      (r.description && r.description.toLowerCase().includes(roleSearchTerm.toLowerCase()))
    )
    .map((r, idx) => ({
      ...r,
      srNo: idx + 1,
      actions: (
        <div>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => {
              // Use the role id for edit navigation
              navigate(`/dashboard/userRole/edit/${r.id}`);
            }}
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    }));

  useEffect(() => {
    setPage(1);
  }, [userSearchTerm]);

  // refetch when page or limit change
  useEffect(() => {
    fetchUsers(page, limit);
    // eslint-disable-next-line
  }, [page, limit]);

    console.log(roles);
  // User Modal Submit
  const handleUserSubmit = async () => {
    const errors = {};
    ['roleId','firstName','lastName','status','emailId'].forEach(f => {
      if (!userModalData[f]) errors[f] = "Required";
    });
    setUserErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setUserSubmitting(true);

  // Get userType from localStorage at time of user creation
  const userType = localStorage.getItem("userType") || 'Admin';

    // Prepare payload for API - ensure all values are properly formatted
    const payload = {
      firstName: String(userModalData.firstName || '').trim(),
      lastName: String(userModalData.lastName || '').trim(),
      email: String(userModalData.emailId || '').trim(),
      roleId: String(userModalData.roleId || '').trim(),
      status: String(userModalData.status || 'Active').trim(),
      note: String(userModalData.note || '').trim(),
      userType: String(userType).trim(),
    };

    console.log('User creation payload:', payload); // For debugging

    try {
      let res;
      if (userModalData && userModalData.id) {
        // Update existing user
        res = await axios.put(`/api/user/${userModalData.id}`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        // Create new user
        res = await axios.post('/api/user/create', payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      }
      const data = res.data;
      if (data.Status === 1) {
        toastSuccess(data.Message || (userModalData && userModalData.id ? 'User updated successfully!' : 'User created successfully!'));
        // Refresh user list using fetchUsers()
        await fetchUsers(page, limit);
        setShowUserModal(false);
        setUserModalData({
          firstName: '',
          lastName: '',
          emailId: '',
          roleId: '',
          status: 'Active',
          note: '',
          userType: localStorage.getItem("userType") || 'Company',
        });
      } else {
        const errorMessage = data.Message || (userModalData && userModalData.id ? "Failed to update user" : "Failed to create user");
        console.error('User save failed:', errorMessage);
        toastError(errorMessage);
        setUserErrors({ api: errorMessage });
      }
    } catch (err) {
      console.error('User save error:', err);
      const errorMessage = err.response?.data?.Message || err.message || (userModalData && userModalData.id ? "Failed to update user" : "Failed to create user");
      toastError(errorMessage);
      setUserErrors({ api: errorMessage });
    }
    setUserSubmitting(false);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0">User & Role</h2>
        <div className="d-flex gap-2 align-items-center">
          {activeTab === 'user' ? (
            <button className="btn btn-primary" onClick={() => { 
              const currentUserType = localStorage.getItem("userType") || 'Admin';
              setUserModalData({
                firstName: '',
                lastName: '',
                emailId: '',
                roleId: '',
                status: 'Active',
                note: '',
                userType: currentUserType,
              }); 
              setShowUserModal(true); 
            }}>
              <FaPlus className="me-1" /> Create New User
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => navigate('/dashboard/userRole/create')}>
              <FaPlus className="me-1" /> Create New Role
            </button>
          )}

        </div>
      </div>

      {/* Tabs */}
      <div className="mb-3">
        <button
          className={`btn px-3 py-2 me-2 ${
            activeTab === 'user' ? "btn-primary" : "btn-light"
          }`}
          style={{ borderRadius: "6px", fontWeight: 500 }}
          onClick={() => setActiveTab('user')}
        >
          User {activeTab === 'user' && `(${users.length})`}
        </button>
        <button
          className={`btn px-3 py-2 me-2 ${
            activeTab === 'role' ? "btn-primary" : "btn-light"
          }`}
          style={{ borderRadius: "6px", fontWeight: 500 }}
          onClick={() => setActiveTab('role')}
        >
          Role {activeTab === 'role' && `(${roles.length})`}
        </button>
      </div>

      {activeTab === 'user' ? (
        <>
          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-md-6"></div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Type to search..."
                  value={userSearchTerm}
                  onChange={e => setUserSearchTerm(e.target.value)}
                  style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
          </div>
          {/* User Table */}
          <CommonTable
            columns={userFields}
            data={userTableData}
            itemsPerPageOptions={[10, 15, 25, 50]}
            defaultItemsPerPage={15}
            page={page}
            onPageChange={setPage}
            itemsPerPage={limit}
            onItemsPerPageChange={setLimit}
            totalItems={totalUsers}
          />
          {/* User Modal */}
          <CommonModal
            visible={showUserModal}
            title={userModalData && userModalData.id ? "Edit User" : "Add New User"}
            fields={[
              { name: 'firstName', label: 'First Name', type: 'text', placeholder: 'First Name' },
              { name: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Last Name' },
              { name: 'emailId', label: 'Email ID', type: 'text', placeholder: 'Email ID' },
              { name: 'roleId', label: 'Role Id', type: 'select', options: roleOptions, placeholder: 'Select Role' },
              { name: 'status', label: 'Status', type: 'select', options: [
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' }
                ], placeholder: 'Select Status'
              },
              { name: 'note', label: 'Note', type: 'textarea', placeholder: 'Note', maxLength: 500 },
            ]}
            values={userModalData}
            onChange={setUserModalData}
            onCancel={() => {
              setShowUserModal(false);
              setUserModalData({
                firstName: '',
                lastName: '',
                emailId: '',
                roleId: '',
                status: 'Active',
                note: '',
                userType: localStorage.getItem("userType") || 'Admin',
              });
              setUserErrors({});
            }}
            onSubmit={handleUserSubmit}
            submitLabel={userSubmitting ? (userModalData && userModalData.id ? "Updating..." : "Saving...") : (userModalData && userModalData.id ? "Update" : "Save")}
            cancelLabel="Cancel"
            requiredFields={['roleId','firstName','lastName','status','emailId']}
            errors={userErrors}
            disabled={userSubmitting}
          />

          <UserView
            visible={viewUserModal}
            user={selectedUser}
            onClose={() => setViewUserModal(false)}
          />
        </>
      ) : (
        <>
          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-md-6"></div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Type to search..."
                  value={roleSearchTerm}
                  onChange={e => setRoleSearchTerm(e.target.value)}
                  style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
          </div>
          {/* Role Table */}
          <CommonTable
            columns={roleFields}
            data={roleTableData}
            itemsPerPageOptions={[10, 15, 25, 50]}
            defaultItemsPerPage={15}
            page={page}
            onPageChange={setPage}
            itemsPerPage={limit}
            onItemsPerPageChange={setLimit}
          />
        </>
      )}
    </div>
  );
}

export default UserRole;